/**
 * Módulo FHIR centralizado.
 *
 * Objetivo:
 * - Definir clients FHIR semânticos para os diferentes contextos do produto
 *   (ex.: administração e workflow), reaproveitando a factory genérica.
 * - Evitar espalhar strings de path (`/admin/fhir`, `/workflow/fhir`) pelo código.
 * - Oferecer um único ponto de entrada FHIR para os demais módulos.
 *
 * Importante:
 * - Este módulo continua sendo de INFRA de integração (tecnologia FHIR),
 *   mas já fala na linguagem do produto ("admin", "workflow").
 * - A UI (`applications/*`) não deve importar diretamente estes clients;
 *   ela deve falar com serviços de domínio (ex.: `patientService`).
 */

import { currentFeatures } from "@/configs/Settings";
import { createFhirApi, FhirApi } from "./utils/fhirResourceFactory";

/**
 * Base URL/path para o ehr-poster (POST de recursos clínicos no ehr-runner).
 *
 * - Produção/dev: `VITE_ENDPOINT_POSTER` (ex.: https://.../ehrposter/fhir).
 * - Fallback relativo ao `VITE_BASE_URL` do httpClient, espelhando o padrão `/admin/fhir`.
 *
 * Paridade com `PostQuestionnaireResponse.tsx`: URL `${EHRPOSTER}/QuestionnaireResponse`,
 * headers FHIR + `x-user-token` + `subject-id` / `organization-id` (ver `buildEhrPosterHttpConfig`).
 */
const EHR_POSTER_BASE_PATH = currentFeatures.defaultApp
  ? "/ehrposter/fhir"
  : "/clinical/fhir";

const ehrPosterDefaultHeaders: Record<string, string> = {
  Accept: "application/fhir+json",
  "Content-Type": "application/fhir+json",
};

/**
 * Base path para o servidor FHIR usado em operações de administração.
 *
 * Exemplo de recursos:
 * - Patient, Organization, Practitioner, etc. em contexto administrativo.
 */
const ADMIN_FHIR_BASE_PATH = "/admin/fhir";

/**
 * Base path para o servidor FHIR usado em operações de workflow clínico.
 *
 * Exemplo de recursos:
 * - Task, Encounter, Appointment, etc. em contexto de fluxo de trabalho.
 */
const WORKFLOW_FHIR_BASE_PATH = "/workflow/fhir";

/**
 * Base path para o servidor FHIR usado em operações de agendamento (scheduling).
 *
 * Exemplo de recursos:
 * - Patient e Appointment em contexto de agendamento, incluindo buscas com `_has`.
 */
const SCHEDULING_FHIR_BASE_PATH = currentFeatures.defaultApp
  ? "/scheduling/fhir"
  : "/clinical/fhir";

/**
 * Client FHIR para o contexto de administração.
 *
 * - Usa `createFhirApi` com o path `/admin/fhir`.
 * - Exposto como `fhirClients.admin`.
 */
const adminFhirApi: FhirApi = createFhirApi(ADMIN_FHIR_BASE_PATH);

/**
 * Client FHIR para o contexto de workflow.
 *
 * - Usa `createFhirApi` com o path `/workflow/fhir`.
 * - Exposto como `fhirClients.workflow`.
 */
const workflowFhirApi: FhirApi = createFhirApi(WORKFLOW_FHIR_BASE_PATH);

/**
 * Client FHIR para o contexto de scheduling.
 *
 * - Usa `createFhirApi` com o path `/scheduling/fhir`.
 * - Exposto como `fhirClients.scheduling`.
 */
const schedulingFhirApi: FhirApi = createFhirApi(SCHEDULING_FHIR_BASE_PATH, {
  headers: {
    Accept: "application/fhir+json",
  },
});

/**
 * Client FHIR para POST de recursos via ehr-poster (ex.: QuestionnaireResponse).
 *
 * - Path base: `VITE_ENDPOINT_POSTER`.
 * - `withCredentials: false` no default: evita CORS com credenciais em localhost → API absoluta
 *   (o legado autentica com Bearer + headers; ver comentário em `buildEhrPosterHttpConfig`).
 * - Chamadas que precisem de paridade total com o src devem mesclar `buildEhrPosterHttpConfig()`
 *   no `create`/`update` (ex.: `QuestionnaireRespondService`).
 */
const ehrPosterFhirApi: FhirApi = createFhirApi(EHR_POSTER_BASE_PATH, {
  headers: ehrPosterDefaultHeaders,
  withCredentials: false,
});

/**
 * Conjunto de clients FHIR semânticos disponíveis para outros módulos.
 *
 * Regra de uso:
 * - Outros módulos (ex.: `patient`, `task`) podem importar estes clients.
 * - A UI NÃO deve importar diretamente `fhirClients`; ela deve falar com
 *   serviços de domínio que, por sua vez, utilizam estes clients.
 */
export const fhirClients = {
  admin: adminFhirApi,

  workflow: workflowFhirApi,
  scheduling: schedulingFhirApi,
  ehrPoster: ehrPosterFhirApi,
};
